import { VerityApi } from "../../../api/fetch";
import { EditQuizPageProps } from "../../editQuiz";
import useLiveState from "./useLiveState";
import useNavState from "./useNavState";
import useProfileState from "./useProfileState";
import useSettingsState from "./useSettingsState";
import useStudentState from "./useStudentState";
import useSubmitState from "./useSubmitState";
import useThemeState from "./useThemeState";

export type UseEditQuizStateProps = Omit<EditQuizPageProps, "api"> & {
	api: VerityApi;
};

/**
 * Contains just state, refs and functions to update state.
 * SHOULD NOT DEPEND ON OTHER STATE HOOKS (to avoid circular dependency)
 *
 * aka should only depend on EditQuizPageProps
 */
export default function useEditQuizState({
	api,
	quizInfo,
	existingLiveSessions,
	setExistingLiveSessions,
	pendingLiveSessions,
	setPendingLiveSessions,
	pendingLiveSessionIds,
	setPendingLiveSessionIds,
	currFakeLiveSessionId,
	initialLiveSessions,
	assessmentUuids,
	logger,
	setStep,
	step,
	proctoringOption,
	setCurrentProfileId,
	enforcedProfilesOnly,
	proctoringAssessmentConfiguration,
	forceStartOnFirstPage,
	initialProctoringAssessmentConfiguration,
	currentProfileId,
	uploadedExemptedStudentsMap,
	uploadedProfilesMap,
	setUploadedProfilesMap,
	initialProctoringOption,
	courseName,
	defaultProctoringOption,
	setUploadedExemptedStudentsMap,
	quizType,
	existingReferenceMaterialFiles,
	initialReferenceMaterialFiles,
	checkThemeExists,
	onSettingsLoadingChange,
	entryPoint,
}: UseEditQuizStateProps) {
	const { action: settingsAction, state: settingsState } = useSettingsState({
		api,
		quizInfo,
		logger,
		courseName,
		proctoringOption,
		assessmentUuids,
		existingReferenceMaterialFiles,
		initialReferenceMaterialFiles,
		quizType,
		proctoringAssessmentConfiguration,
		onSettingsLoadingChange,
		entryPoint,
	});
	const { action: liveAction, state: liveState } = useLiveState({
		api,
		quizInfo,
		existingLiveSessions,
		setExistingLiveSessions,
		pendingLiveSessions,
		setPendingLiveSessions,
		pendingLiveSessionIds,
		setPendingLiveSessionIds,
		currFakeLiveSessionId,
		initialLiveSessions,
	});
	const {
		action: navAction,
		state: navState,
		internal: navInternal,
	} = useNavState({
		setStep,
		step,
		forceStartOnFirstPage,
		initialProctoringAssessmentConfiguration,
		proctoringAssessmentConfiguration,
	});
	const { action: profileAction, state: profileState } = useProfileState({
		api,
		quizType,
		enforcedProfilesOnly,
		quizInfo,
		setCurrentProfileId,
		setUploadedProfilesMap,
		currentProfileId,
	});
	const { action: studentAction, state: studentState } = useStudentState({
		api,
		quizInfo,
		uploadedExemptedStudentsMap,
		uploadedProfilesMap,
		setUploadedExemptedStudentsMap,
		quizType,
	});
	const { action: themeAction, state: themeState } = useThemeState({
		quizInfo,
		proctoringAssessmentConfiguration,
		quizType,
		checkThemeExists,
	});
	const { action: submitAction, state: submitState } = useSubmitState({
		logger,
		api,
		quizInfo,
		currentProfileId,
		proctoringAssessmentConfiguration,
		proctoringOption,
		initialProctoringAssessmentConfiguration,
		initialProctoringOption,
		defaultProctoringOption,
	});

	return {
		setting: {
			state: settingsState,
			action: settingsAction,
		},
		live: {
			state: liveState,
			action: liveAction,
		},
		nav: {
			state: navState,
			action: navAction,
			internal: navInternal,
		},
		profile: {
			state: profileState,
			action: profileAction,
		},
		student: {
			state: studentState,
			action: studentAction,
		},
		theme: {
			state: themeState,
			action: themeAction,
		},
		submit: {
			state: submitState,
			action: submitAction,
		},
	};
}

export type UseEditQuizStateReturn = ReturnType<typeof useEditQuizState>;
